/**
 * JoinCareTeamModal
 *
 * Modal component for existing users to join a care team using an invitation code.
 * This can be used from Dashboard or any other location in the app.
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { Modal, ModalCloseButton } from '../ui/Modal';

interface JoinCareTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  onJoined?: (caseFileId: string, caseFileName: string) => void;
}

// Invitation validation response (matches backend response shape)
interface ValidateInvitationResponse {
  valid: boolean;
  caseFileName?: string;
  ownerName?: string;
  relationship?: string;
  role?: string;
  expiresAt?: string;
  error?: string;
}

// Structured invitation data for display
interface InvitationDisplayData {
  caseFileName: string;
  ownerName: string;
  relationship: string;
  role: string;
  expiresAt: string;
}

// Invitation acceptance response
interface AcceptInvitationResponse {
  success: boolean;
  caseFileId?: string;
  caseFileName?: string;
  role?: string;
  message?: string;
}

export function JoinCareTeamModal({
  isOpen,
  onClose,
  onJoined,
}: JoinCareTeamModalProps): JSX.Element | null {
  const [inviteCode, setInviteCode] = useState('');
  const [validatedInvitation, setValidatedInvitation] = useState<InvitationDisplayData | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Helper to format role for display
  const formatRole = (role: string): string => {
    const roleMap: Record<string, string> = {
      admin: 'Administrator',
      editor: 'Editor',
      viewer: 'Viewer',
      contact: 'Contact',
    };
    return roleMap[role] || role;
  };

  // Helper to format relationship for display
  const formatRelationship = (relationship: string): string => {
    const relationshipMap: Record<string, string> = {
      self: 'Self (Care Recipient)',
      parent: 'Parent',
      child: 'Child',
      spouse: 'Spouse/Partner',
      sibling: 'Sibling',
      grandparent: 'Grandparent',
      extended: 'Extended Family',
      other: 'Other',
    };
    return relationshipMap[relationship] || relationship;
  };

  const handleValidateInvitation = async () => {
    if (!inviteCode.trim()) {
      setError('Please enter an invitation code');
      return;
    }

    setIsValidating(true);
    setError(null);
    setValidatedInvitation(null);

    try {
      const validateInvitation = httpsCallable<{ invitationCode: string }, ValidateInvitationResponse>(
        functions,
        'validateCareTeamInvitation'
      );
      const result = await validateInvitation({ invitationCode: inviteCode.trim().toUpperCase() });

      if (result.data.valid) {
        // Map flat response to structured display data
        setValidatedInvitation({
          caseFileName: result.data.caseFileName || 'Unknown',
          ownerName: result.data.ownerName || 'Care Team Owner',
          relationship: result.data.relationship || 'other',
          role: result.data.role || 'viewer',
          expiresAt: result.data.expiresAt || '',
        });
      } else {
        setError(result.data.error || 'Invalid or expired invitation code');
      }
    } catch (err: unknown) {
      console.error('Error validating invitation:', err);
      const firebaseError = err as { code?: string; message?: string };
      if (firebaseError.message) {
        const message = firebaseError.message.includes('invalid-argument')
          ? 'Invalid invitation code format'
          : firebaseError.message.includes('not-found')
          ? 'Invitation code not found or has expired'
          : firebaseError.message.replace(/^Firebase:\s*/, '').replace(/\s*\([^)]+\)\s*$/, '')
            || 'Failed to validate invitation code';
        setError(message);
      } else {
        setError('Failed to validate invitation code. Please try again.');
      }
    } finally {
      setIsValidating(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!validatedInvitation) {
      setError('Please validate the invitation code first');
      return;
    }

    setIsJoining(true);
    setError(null);

    try {
      const acceptInvitation = httpsCallable<{ invitationCode: string }, AcceptInvitationResponse>(
        functions,
        'acceptCareTeamInvitation'
      );
      const result = await acceptInvitation({ invitationCode: inviteCode.trim().toUpperCase() });

      if (result.data.success) {
        setSuccessMessage(
          `Successfully joined ${result.data.caseFileName || 'the care team'} as ${result.data.role || 'a team member'}!`
        );

        // Notify parent component
        if (onJoined && result.data.caseFileId) {
          onJoined(result.data.caseFileId, result.data.caseFileName || 'Care Team');
        }
      } else {
        setError(result.data.message || 'Failed to accept invitation');
      }
    } catch (err: unknown) {
      console.error('Error accepting invitation:', err);
      // Firebase callable function errors have a specific structure
      const firebaseError = err as { code?: string; message?: string; details?: unknown };
      if (firebaseError.message) {
        // Extract meaningful message from Firebase error
        // Firebase errors often have format: "Firebase: Error (auth/...)" or just the message
        const message = firebaseError.message
          .replace(/^Firebase:\s*/, '')
          .replace(/\s*\([^)]+\)\s*$/, '');
        setError(message || 'Failed to join care team. Please try again.');
      } else {
        setError('Failed to join care team. Please try again.');
      }
    } finally {
      setIsJoining(false);
    }
  };

  const handleInviteCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCode = e.target.value.toUpperCase();
    setInviteCode(newCode);
    if (validatedInvitation) {
      setValidatedInvitation(null);
    }
    setError(null);
  };

  const handleClose = () => {
    // Reset state on close
    setInviteCode('');
    setValidatedInvitation(null);
    setError(null);
    setSuccessMessage(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Join a Care Team"
      size="md"
      closeDisabled={isJoining}
    >
      <div>
            {successMessage ? (
              // Success State
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-ink mb-2">
                  You're In!
                </h3>
                <p className="text-muted mb-6">{successMessage}</p>
                <ModalCloseButton className="btn-primary">
                  Done
                </ModalCloseButton>
              </div>
            ) : (
              // Form State
              <div className="space-y-4">
                <p className="text-muted text-sm">
                  Enter the invitation code you received from a care team owner.
                </p>

                {/* Invitation Code Input */}
                <div>
                  <label htmlFor="modal-invite-code" className="label">
                    Invitation Code
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="modal-invite-code"
                      type="text"
                      value={inviteCode}
                      onChange={handleInviteCodeChange}
                      className={`input flex-1 font-mono tracking-wider uppercase ${
                        validatedInvitation ? 'border-green-500 bg-green-50' : ''
                      }`}
                      placeholder="e.g., ABC12345"
                      maxLength={8}
                      disabled={isValidating || isJoining}
                    />
                    {!validatedInvitation && (
                      <button
                        type="button"
                        onClick={handleValidateInvitation}
                        disabled={!inviteCode.trim() || isValidating}
                        className="btn-secondary whitespace-nowrap"
                      >
                        {isValidating ? (
                          <>
                            <span className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                            Checking...
                          </>
                        ) : (
                          'Verify'
                        )}
                      </button>
                    )}
                  </div>
                  <p className="helper-text">8 characters (letters and numbers)</p>
                </div>

                {/* Validated Invitation Preview */}
                {validatedInvitation && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-green-800 text-sm mb-2">
                          Invitation Verified
                        </h4>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span className="text-green-700">CaseFile:</span>
                            <span className="font-medium text-green-900">
                              {validatedInvitation.caseFileName}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-green-700">Invited by:</span>
                            <span className="font-medium text-green-900">
                              {validatedInvitation.ownerName || 'Care Team Owner'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-green-700">Your Role:</span>
                            <span className="font-medium text-green-900">
                              {formatRole(validatedInvitation.role)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-green-700">Relationship:</span>
                            <span className="font-medium text-green-900">
                              {formatRelationship(validatedInvitation.relationship)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Display */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3" role="alert">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <p className="text-red-800 text-sm">{error}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          {!successMessage && (
            <div className="flex gap-3 p-4 border-t border-soft-neutral">
              <ModalCloseButton
                disabled={isJoining}
                className="btn-secondary flex-1"
              >
                Cancel
              </ModalCloseButton>
              {validatedInvitation ? (
                <button
                  type="button"
                  onClick={handleAcceptInvitation}
                  disabled={isJoining}
                  className="btn-primary flex-1"
                >
                  {isJoining ? (
                    <>
                      <span className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                      Joining...
                    </>
                  ) : (
                    <>
                      Join Team <ArrowRight className="w-4 h-4 ml-1 inline" />
                    </>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleValidateInvitation}
                  disabled={!inviteCode.trim() || isValidating}
                  className="btn-primary flex-1"
                >
                  Verify Code
                </button>
              )}
            </div>
          )}
    </Modal>
  );
}

export default JoinCareTeamModal;
